import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DeleteConfirmDialog } from "../../components/DeleteConfirmDialog";
import {
  bulkDeleteMembers,
  deleteMember,
  listMembers,
  updateMember,
} from "../../lib/adminApi";

export function AdminMembersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // `selected` is a Set of member IDs, keyed off the same `m.id` React uses. We keep it as
  // a Set so add/remove is O(1) and the header master checkbox can compare against
  // `filtered.length` in one line.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Dialog state: either a single-row delete `{row}` or the bulk-delete case (a Set of IDs).
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listMembers());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load members.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (!q) return true;
        const hay = `${r.email || ""} ${r.mobile_e164 || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      }),
    [rows, q]
  );

  // Whenever the search filter changes, drop any previously-selected IDs that are no
  // longer visible — otherwise "Delete selected" could nuke rows the admin can't see.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(filtered.map((r) => r.id));
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [filtered]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someVisibleSelected = filtered.some((r) => selected.has(r.id)) && !allVisibleSelected;

  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const r of filtered) next.delete(r.id);
        return next;
      }
      const next = new Set(prev);
      for (const r of filtered) next.add(r.id);
      return next;
    });
  }

  async function toggle(row) {
    const next = row.access_status === "active" ? "disabled" : "active";
    try {
      const updated = await updateMember(row.id, { access_status: next });
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    }
  }

  async function remove(row) {
    setDialog({ mode: "single", row });
  }

  async function confirmSingleDelete() {
    const row = dialog?.row;
    if (!row) return;
    setBulkDeleting(true);
    try {
      await deleteMember(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      toast.success("Member deleted.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete.");
    } finally {
      setBulkDeleting(false);
      setDialog(null);
    }
  }

  async function removeSelected() {
    if (!selected.size) return;
    setDialog({ mode: "bulk", ids: [...selected] });
  }

  async function confirmBulkDelete() {
    const ids = dialog?.ids || [];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      const { deleted } = await bulkDeleteMembers(ids);
      const removed = new Set(ids);
      setRows((prev) => prev.filter((r) => !removed.has(r.id)));
      setSelected(new Set());
      toast.success(`${deleted} member${deleted === 1 ? "" : "s"} deleted.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete the selected members.");
    } finally {
      setBulkDeleting(false);
      setDialog(null);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-members-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-gold">Members</p>
        <h1 className="mt-1 text-3xl font-semibold text-text">All members</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted">
          Every user who has completed OTP login at least once. Click a member to view their saved profile.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold">Members ({rows.length})</h3>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email / mobile"
            data-testid="members-search"
            className="w-full sm:w-72"
          />
        </div>

        {/* Bulk action bar — collapses to zero height when nothing is selected so the
            table doesn't jitter on filter changes. */}
        {selected.size > 0 && (
          <div
            className="flex items-center justify-between border-b border-border bg-elevated/50 px-4 py-2.5"
            data-testid="members-bulk-bar"
          >
            <div className="text-sm text-text">
              <span className="font-semibold text-gold">{selected.size}</span> selected
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected(new Set())}
                data-testid="members-bulk-clear"
              >
                Clear
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={removeSelected}
                disabled={bulkDeleting}
                data-testid="members-bulk-delete"
              >
                {bulkDeleting ? (
                  <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Deleting…</>
                ) : (
                  <><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete selected</>
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="w-12 px-4 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all visible"
                    checked={allVisibleSelected}
                    // `indeterminate` isn't part of React's attribute model — we set it via ref.
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleAllVisible}
                    disabled={filtered.length === 0}
                    data-testid="members-select-all"
                    className="h-4 w-4 cursor-pointer accent-gold"
                  />
                </th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Mobile</th>
                <th className="px-4 py-3">Profile</th>
                <th className="px-4 py-3">Access</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-muted">No members yet.</td></tr>
              ) : (
                filtered.map((m) => (
                  <tr
                    key={m.id}
                    className={`border-b border-border/70 last:border-b-0 hover:bg-elevated/60 ${
                      selected.has(m.id) ? "bg-gold/5" : ""
                    }`}
                    data-testid={`member-row-${m.id}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select member ${m.email || m.mobile_e164}`}
                        checked={selected.has(m.id)}
                        onChange={() => toggleOne(m.id)}
                        data-testid={`member-select-${m.id}`}
                        className="h-4 w-4 cursor-pointer accent-gold"
                      />
                    </td>
                    <td className="px-4 py-3 text-text">{m.email || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-text">{m.mobile_e164 || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {m.has_profile ? (
                        <span className="rounded-full bg-gold/10 px-2 py-0.5 uppercase tracking-widest text-gold">Yes</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                        m.access_status === "active" ? "bg-gold/10 text-gold" : "bg-muted/20 text-muted"
                      }`}>
                        {m.access_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Link to={`/admin/members/${m.id}`}>
                          <Button size="sm" variant="outline" data-testid={`view-${m.id}`}>View</Button>
                        </Link>
                        <Button size="sm" variant="outline" onClick={() => toggle(m)} data-testid={`member-toggle-${m.id}`}>
                          {m.access_status === "active" ? "Disable" : "Activate"}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(m)} data-testid={`member-delete-${m.id}`}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DeleteConfirmDialog
        open={dialog?.mode === "single"}
        busy={bulkDeleting}
        onCancel={() => setDialog(null)}
        onConfirm={confirmSingleDelete}
        title={`Delete ${dialog?.row?.email || dialog?.row?.mobile_e164 || "member"}?`}
        description="This removes the member, their financial profile and every saved scenario. This can't be undone."
        confirmLabel="Yes, delete"
        testId="member-single-delete-dialog"
      />
      <DeleteConfirmDialog
        open={dialog?.mode === "bulk"}
        busy={bulkDeleting}
        onCancel={() => setDialog(null)}
        onConfirm={confirmBulkDelete}
        title={`Delete ${dialog?.ids?.length || 0} member${dialog?.ids?.length === 1 ? "" : "s"}?`}
        description={`Each member's financial profile and saved scenarios will be permanently removed. To prevent accidental bulk deletion, please type DELETE below to confirm.`}
        requireType
        confirmLabel="Yes, delete all"
        testId="member-bulk-delete-dialog"
      />
    </div>
  );
}
